import { apiGet, apiPost } from '../../lib/apiClient'
import type { IMetadataService, GitDeployStatus, ComponentSource } from './IMetadataService.interface'
import type {
  MetadataSnapshot,
  MetadataComparison,
  ComponentDiffDetail,
  MetadataCapability,
  MetadataSelector,
  ComponentListing,
  MetadataTypeInfo,
  CompareStreamEvent,
  ComponentChangeKind,
  ComponentDiff,
  TypeRunCounts,
} from '@stride/shared'
import type { CompareSource } from '@stride/shared'

/**
 * How often to ask the API whether a compare has finished, when the socket has not said so.
 *
 * Four seconds: long enough that a healthy socket almost always wins the race (server-side compares of a
 * handful of components take 1-2s), short enough that a dropped socket costs a pause rather than the flow.
 */
const POLL_MS = 4000

/**
 * Per-type counts, tallied from the stored `change` label.
 *
 * `change` is the field — my first version counted a `changeKind` that does not exist on `ComponentDiff`, and
 * the unit tests passed because vitest transpiles without typechecking and the fixture invented the same
 * wrong name. `turbo build` caught it. Worth the note: a test fixture that mirrors a mistaken assumption
 * confirms the assumption rather than the code.
 */
function countsOf(diffs: readonly ComponentDiff[]): TypeRunCounts {
  const counts: TypeRunCounts = { added: 0, changed: 0, deleted: 0, unchanged: 0 }
  for (const d of diffs) {
    if (d.change === 'added') counts.added += 1
    else if (d.change === 'changed') counts.changed += 1
    else if (d.change === 'deleted') counts.deleted += 1
    else counts.unchanged += 1
  }
  return counts
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? ''

export class HttpMetadataService implements IMetadataService {
  private _socket: import('socket.io-client').Socket | null = null

  private async getSocket(): Promise<import('socket.io-client').Socket> {
    if (this._socket) return this._socket
    const { connectSocket } = await import('../../lib/socket')
    this._socket = connectSocket({ url: API_BASE || '' })
    return this._socket
  }

  capabilities(): MetadataCapability[] {
    return ['retrieve', 'compare', 'partial-refresh']
  }

  describeTypes(connectionId: string): Promise<MetadataTypeInfo[]> {
    return apiGet<MetadataTypeInfo[]>(
      `/api/metadata/types?connectionId=${encodeURIComponent(connectionId)}`,
    )
  }

  retrieve(connectionId: string, types: string[]): Promise<MetadataSnapshot> {
    return apiPost<MetadataSnapshot>('/api/metadata/retrieve', { connectionId, types })
  }

  listSnapshots(sourceRef?: string): Promise<MetadataSnapshot[]> {
    const qs = sourceRef ? `?sourceRef=${encodeURIComponent(sourceRef)}` : ''
    return apiGet<MetadataSnapshot[]>(`/api/metadata/snapshots${qs}`)
  }

  listComponents(connectionId: string, types: string[]): Promise<ComponentListing[]> {
    return apiPost<ComponentListing[]>('/api/metadata/list', { connectionId, types })
  }

  listBranchComponents(
    connectionId: string,
    repo:         string,
    branch:       string,
  ): Promise<ComponentListing[]> {
    const [owner = '', name = ''] = repo.split('/')
    const qs = `?connectionId=${encodeURIComponent(connectionId)}&branch=${encodeURIComponent(branch)}`
    return apiGet<ComponentListing[]>(
      `/api/git/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/branch-components${qs}`,
    )
  }

  compare(
    left:       CompareSource,
    right:      CompareSource,
    selectors?: MetadataSelector[],
  ): Promise<MetadataComparison> {
    return apiPost<MetadataComparison>('/api/metadata/compare', {
      left,
      right,
      ...(selectors !== undefined && { selectors }),
    })
  }

  getDiffDetail(
    comparisonId: string,
    type:         string,
    fullName:     string,
  ): Promise<ComponentDiffDetail> {
    const qs = `?type=${encodeURIComponent(type)}&fullName=${encodeURIComponent(fullName)}`
    return apiGet<ComponentDiffDetail>(`/api/metadata/comparisons/${comparisonId}/detail${qs}`)
  }

  getComponentSource(
    source:   CompareSource,
    type:     string,
    fullName: string,
  ): Promise<ComponentSource> {
    return apiPost<ComponentSource>('/api/metadata/component-source', { source, type, fullName })
  }

  refreshType(comparisonId: string, type: string): Promise<MetadataComparison> {
    return apiPost<MetadataComparison>(
      `/api/metadata/comparisons/${comparisonId}/refresh`,
      { type },
    )
  }

  captureFromCompare(
    comparisonId: string,
    req: { type: string; fullName: string; gitConnectionId: string; repo: string; branch: string; fromBranch?: string; commitMessage?: string },
  ): Promise<{ type: string; fullName: string; branch: string; commitSha: string; paths: string[] }> {
    return apiPost(`/api/metadata/comparisons/${comparisonId}/capture`, req)
  }

  // ── FX2 Cherry-pick deploy ────────────────────────────────────────────────

  deployComponents(params: {
    comparisonId:        string
    componentKeys:       string[]
    sourceConnectionId:  string
    targetConnectionId:  string
  }): Promise<{ deployRunId: string }> {
    return apiPost<{ deployRunId: string }>('/api/metadata/deploy', params)
  }

  deployComponentsDirect(params: {
    components:          Array<{ type: string; fullName: string; changeKind?: ComponentChangeKind }>
    sourceConnectionId:  string
    targetConnectionId:  string
    mode?:               'deploy' | 'validate'
    destructiveAck?:     boolean
  }): Promise<{ deployRunId: string }> {
    return apiPost<{ deployRunId: string }>('/api/metadata/deploy/direct', params)
  }

  // ── FX3 Git-backed deploy ─────────────────────────────────────────────────

  deployViaGit(params: {
    comparisonId:        string
    componentKeys:       string[]
    sourceConnectionId:  string
    targetConnectionId:  string
    gitConnectionId:     string
    repo:                string
    commitMessage?:      string
    prTitle?:            string
    prBody?:             string
  }): Promise<{
    pullRequestUrl: string
    deployRunId:    string
    branch:         string
    workingBranch:  string
  }> {
    return apiPost('/api/metadata/deploy/git', params)
  }

  // ── Brief 3 — stepped deploy (commit + branch-source deploy) ──────────────

  commitSelectionToBranch(params: {
    comparisonId:    string
    componentKeys:   string[]
    repo:            string
    branch:          string
    gitConnectionId: string
    commitMessage?:  string
    destructiveAck?: boolean
  }): Promise<{ fileCount: number; warnings: string[]; commitSha?: string }> {
    return apiPost('/api/metadata/deploy/git/commit', params)
  }

  deployFromBranch(params: {
    repo:               string
    branch:             string
    gitConnectionId:    string
    targetConnectionId: string
    testLevel?:         'NoTestRun' | 'RunLocalTests' | 'RunAllTestsInOrg'
  }): Promise<{ deployRunId: string }> {
    return apiPost<{ deployRunId: string }>('/api/metadata/deploy/git/deploy-branch', params)
  }

  // ── FX3 Stage 2 — deploy-on-merge ─────────────────────────────────────────

  getGitDeployStatus(runId: string): Promise<GitDeployStatus> {
    return apiGet<GitDeployStatus>(`/api/metadata/deploy/git/${runId}`)
  }

  triggerGitDeploy(runId: string): Promise<{ deployRunId: string }> {
    // Let the ApiError (incl. 409 "Merge the PR first.") propagate to the UI.
    return apiPost<{ deployRunId: string }>(`/api/metadata/deploy/git/${runId}/deploy`, {})
  }

  validateComponents(params: {
    comparisonId:        string
    componentKeys:       string[]
    sourceConnectionId:  string
    targetConnectionId:  string
  }): Promise<{ deployRunId: string }> {
    return apiPost<{ deployRunId: string }>('/api/metadata/deploy', { ...params, mode: 'validate' })
  }

  // ── F4 Streaming ──────────────────────────────────────────────────────────

  startCompare(
    left:       CompareSource,
    right:      CompareSource,
    selectors?: MetadataSelector[],
  ): Promise<{ comparisonId: string }> {
    return apiPost<{ comparisonId: string }>('/api/metadata/compare', {
      left,
      right,
      ...(selectors !== undefined && { selectors }),
    })
  }

  /**
   * Subscribe to a compare — over the socket, with a REST fallback that makes a missed event survivable.
   *
   * ── Why the fallback exists ──
   * The result is delivered as Socket.IO events. If the socket never connects, nothing arrives and the screen
   * waits for ever on work that has already finished. That is not hypothetical: socket auth refused every
   * member who did not also belong to the `default` project, so a compare completing server-side in 1.5
   * seconds showed as a spinner that never stopped, and was reported as "compare is slow". The auth bug is
   * fixed; this makes the class of failure cost seconds instead of the whole flow — a socket can also be
   * dropped by a proxy, a sleeping laptop, or a subscribe that lands after the run finished.
   *
   * The fallback REPLAYS the stored comparison as the event sequence a caller already handles — a `type.done`
   * per type, then `compare.done` — rather than inventing a shortcut. Callers build their state from those
   * events, so synthesising only `compare.done` would finish the spinner and show an empty diff, which is a
   * worse failure than hanging: it looks like a real answer.
   *
   * Whichever path arrives first wins, and `delivered` makes the other a no-op. Live per-type progress still
   * comes from the socket; this only answers "did it finish".
   */
  subscribeCompare(
    comparisonId: string,
    onEvent:      (e: CompareStreamEvent) => void,
  ): () => void {
    let socket: import('socket.io-client').Socket | null = null
    let cancelled = false
    let delivered = false

    const deliver = (e: CompareStreamEvent): void => {
      if (cancelled) return
      if (e.kind === 'compare.done') delivered = true
      onEvent(e)
    }

    void this.getSocket().then((s) => {
      if (cancelled) return
      socket = s
      s.emit('compare:subscribe', comparisonId)
      s.on(`compare:event:${comparisonId}`, deliver)
    }).catch(() => { /* no socket — the poll below is the whole path */ })

    // 404 while the compare is still running: nothing is stored until it completes, so a miss is "not yet"
    // rather than a failure, and polling simply continues.
    const poll = globalThis.setInterval(() => {
      if (cancelled || delivered) return
      void apiGet<MetadataComparison>(`/api/metadata/comparisons/${comparisonId}`)
        .then((c) => {
          if (cancelled || delivered) return
          delivered = true
          const byType = new Map<string, ComponentDiff[]>()
          for (const d of c.diffs) byType.set(d.type, [...(byType.get(d.type) ?? []), d])
          for (const [type, diffs] of byType) {
            deliver({ kind: 'type.done', comparisonId, type, diffs, counts: countsOf(diffs) })
          }
          deliver({ kind: 'compare.done', comparisonId, summary: c.summary })
        })
        .catch(() => { /* not finished yet, or unreachable — keep waiting */ })
    }, POLL_MS)

    return () => {
      cancelled = true
      globalThis.clearInterval(poll)
      if (socket) {
        socket.emit('compare:unsubscribe', comparisonId)
        socket.off(`compare:event:${comparisonId}`, deliver)
      }
    }
  }
}
